import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { PLAY_INFO_ID } from '@/const/mission';
import { IBot, IScene, IStory, StoryLayout } from '@/interface/module';
import { CgiErrorCode } from '@/services/cgi';
import { fetchStoryDetail, fetchStoryList } from '@/services/story';

export interface StoryState {
  storyInfo: IStory;
  currTab: string;
  storyList: IStory[];
  bots: Partial<IBot>[];
  scenes: IScene[];
  endStep: number;
  loading: boolean;
  errors: string;
  playBots: Partial<IBot>[];
  commonBots: Partial<IBot>[];
}

const initialState: StoryState = {
  currTab: 'base',
  loading: false,
  storyInfo: {
    id: '',
    storyId: '',
    name: '未命名',
    background: '',
    intro: '',
    backgroundUrl: {},
    subtitle: '',
    image: {},
    spaceId: '',
    playerInfo: { name: '', avatar: {} },
    sceneList: [],
    createdBy: '',
    tagIds: [],
    updatedBy: '',
    materialMap: {},
    botPromptList: [],
    visibility: 3,
    itemCardList: [],
    layout: StoryLayout.LAYOUT_VERTICAL,
    bgm: {
      url: {
        url: '',
        localPath: '',
        name: '',
      },
      autoPlay: false,
      loopPlay: true,
    },
  },
  playBots: [],
  storyList: [],
  bots: [],
  scenes: [],
  // new state
  endStep: 3,
  errors: '',
  commonBots: [],
};

export const getStoryDetail = createAsyncThunk('story/detail', fetchStoryDetail);

export const getStoryList = createAsyncThunk('story/list', fetchStoryList);

export const story = createSlice({
  name: 'story',

  initialState,

  reducers: {
    clearStory() {
      return initialState;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setStoryErrors(state, action) {
      state.errors = action.payload;
    },
    createNewStory(state) {
      state.storyInfo = initialState.storyInfo;
    },

    setCurrTab(state, { payload }) {
      state.currTab = payload;
    },

    updateStoryInfo(state, { payload }) {
      state.storyInfo = { ...payload };
    },
    changeStoryBot(state, { payload }) {
      const { type, value, id, botId } = payload;
      const { bots = [] } = state;
      const botIndex = bots.findIndex(item => (item?.id === botId || (id && item?.id === id)));

      const tmpValue: IBot = type === 'name' && !value ? `角色${botIndex + 1}` : value;
      state.bots[botIndex] = tmpValue;
    },
    updateStoryScene(state, { payload }) {
      const { sceneId, id } = payload;
      const { scenes } = state;
      const sceneIndex = scenes.findIndex(item => (item?.sceneId === sceneId || (id && item?.id === id)));
      state.scenes[sceneIndex] = { ...payload };
    },
    deleteScene(state, { payload }) {
      const { id, sceneId } = payload;
      const { scenes } = state;
      const sceneIndex = scenes.findIndex(item => (item?.sceneId === sceneId || (id && item?.id === id)));
      if (sceneIndex > 0) {
        state.currTab = `scene-${scenes[sceneIndex - 1].sceneId}`;
      } else if (state.scenes.length > 1) {
        state.currTab = `scene-${state.scenes[sceneIndex + 1].sceneId}`;
      } else {
        state.currTab = 'base';
      }
      state.scenes.splice(sceneIndex, 1);
    },

    insertBot(state, { payload }) {
      const { bots } = state;
      const botsList = bots.concat(payload.bot);
      state.bots = botsList;
    },

    insertScene(state) {
      const { scenes } = state;
      const { length } = scenes;
      const scene = {
        sceneId: `scene_${dayjs().valueOf()}`,
        name: `章节${length + 1}`,
        events: [],
      } as unknown as IScene;
      state.scenes.splice(length, 0, scene);
      state.currTab = `scene-${scene.sceneId}`;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getStoryDetail.fulfilled, (state, { payload }) => {
      if (payload?.code === CgiErrorCode.NO_ERROR) {
        const botList = payload?.storyInfo?.botPromptList?.map(e => ({
          ...e,
          id: e?.id,
        })) || [];

        const playBots: Partial<IBot>[] = [{
          id: PLAY_INFO_ID,
          name: payload?.storyInfo?.playerInfo?.name,
        }];
        return ({
          ...state,
          loading: false,
          storyInfo: {
            ...payload?.storyInfo,
            name: payload?.storyInfo?.name || '未命名',
            layout: payload?.storyInfo?.layout || StoryLayout.LAYOUT_VERTICAL,
          },
          scenes: payload?.scenes,
          playBots,
          bots: botList,
          commonBots: playBots.concat(botList)?.filter((e: Partial<IBot>) => (e.id) && e.name),
        });
      }
    });
    builder.addCase(getStoryList.fulfilled, (state, { payload }) => {
      state.storyList = payload.list as IStory[];
    });
  },
});

export const {
  clearStory,
  createNewStory,
  setCurrTab,
  // changeStoryFields,
  changeStoryBot,
  // changeStoryScene,
  updateStoryScene,
  deleteScene,
  insertBot,
  insertScene,
  updateStoryInfo, // 更新story 数据
  // new
  setLoading,
  setStoryErrors,
} = story.actions;

export default story.reducer;
